import React, { useRef, useState } from "react";
import { Table, Modal } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { ToolTip } from "../../AbstractElements";
import { useLab } from "../../_helper/PharmacyProvider";
import { formatDoctorDisplayDate } from "../../utils/dateTime/dateFormat";
import { getFileUrl } from "../../utils/getFileUrl";

const formatTests = (investigation) => {
  if (!investigation || !investigation.length) return "No tests";

  return investigation.map((inv) => (
    <div key={inv.suggestion_reason || Math.random()}>
      {inv.suggestions && inv.suggestions.length > 0 ? (
        inv.suggestions.map((suggestion) => (
          <div key={suggestion.category_name} style={{ marginBottom: "4px" }}>
            <strong>{suggestion.category_name}:</strong>&nbsp;
            {suggestion.examinations && suggestion.examinations.length > 0
              ? suggestion.examinations
                .map((exam, examIndex) => `${examIndex + 1}) ${exam.name}`)
                .join(", ")
              : "No tests"}
          </div>
        ))
      ) : (
        <div>No suggestions</div>
      )}
    </div>
  ));
};

const getHospitalPatientId = (suggestion) => {
  const patient = suggestion?.patient || {};
  return (
    patient._id ||
    patient.id ||
    patient.patient_id ||
    patient.patientId ||
    suggestion?.patient_id ||
    suggestion?.patientId ||
    null
  );
};

const SuggestionTable = ({
  suggestions = [],
  loading = false,
  showText,
  showAddTest = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentLab } = useLab();
  const [tooltipOpen, setTooltipOpen] = useState({});
  const [modal, setModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const eyeButtonRefs = useRef({});
  const pdfButtonRefs = useRef({});

  const toggleTooltip = (id) => {
    setTooltipOpen((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleView = (suggestion) => {
    setSelectedItem(suggestion);
    setModal(true);
  };

  const handleViewPdf = async (suggestion) => {
    if (!suggestion?.investigation_suggestion_pdf) return;
    try {
      const pdfUrl = await getFileUrl(suggestion.investigation_suggestion_pdf);
      if (pdfUrl) {
        window.open(pdfUrl, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      console.error("Error opening suggestion PDF:", error);
    }
  };

  const handleAddTest = () => {
    const patient = selectedItem?.patient;
    if (!patient) {
      toast.error(t("Patient details are incomplete"));
      return;
    }

    const hospitalPatientId = getHospitalPatientId(selectedItem);
    const healthId = patient.health_id || patient.uniqueId || "";
    const patientName =
      patient.name ||
      patient.full_name ||
      patient.fname ||
      [patient.first_name, patient.middle_name, patient.last_name]
        .filter(Boolean)
        .join(" ");

    // Search term for lab-patient/master (needs enough chars to search)
    const patientSearch =
      healthId ||
      patientName ||
      patient.mobile ||
      patient.contact ||
      hospitalPatientId;

    if (!hospitalPatientId && !healthId) {
      toast.error(t("Patient details are incomplete"));
      return;
    }

    const prefill = {
      fromDashboardSuggestion: true,
      customerType: "hospitalClinic",
      hospitalPatientId,
      healthId,
      patientSearch: String(patientSearch || ""),
      suggestions: selectedItem ? [selectedItem] : [],
    };

    try {
      sessionStorage.setItem(
        "dashboardSuggestionPrefill",
        JSON.stringify(prefill)
      );
    } catch (error) {
      console.error("Unable to store dashboard suggestion prefill:", error);
    }

    navigate(`${process.env.PUBLIC_URL}/search-patient`, { state: prefill });
    setModal(false);
  };

  return (
    <>
      {showText == true && (
        <h6 className="mb-3 tw-font-semibold">
          {t("Investigation Suggestions")}
        </h6>
      )}
      {loading ? (
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : suggestions.length === 0 ? (
        <p className="text-center text-muted">{t("No suggestions found")}</p>
      ) : (
        <Table
          striped
          bordered
          style={{ width: "100%", tableLayout: "fixed" }}
        >
          <colgroup>
            <col style={{ width: "46%" }} />
            <col style={{ width: "24%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "14%" }} />
          </colgroup>
          <thead className="thead-light">
            <tr>
              <th style={{ whiteSpace: "normal" }}>{t("Tests")}</th>
              <th style={{ whiteSpace: "normal" }}>{t("CLINICDOTS Health ID")}</th>
              <th style={{ whiteSpace: "normal" }}>{t("Date")}</th>
              <th style={{ whiteSpace: "normal", textAlign: "center" }}>
                {t("Action")}
              </th>
            </tr>
          </thead>
          <tbody>
            {suggestions.map((suggestion, idx) => (
              <tr key={suggestion._id || idx}>
                <td
                  style={{
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  {formatTests(suggestion.investigation)}
                </td>
                <td style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                  {suggestion.patient?.health_id?.match(/.{1,4}/g).join(" ") || "N/A"}
                </td>
                <td style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                  {formatDoctorDisplayDate(suggestion.createdAt, currentLab) || "-"}
                </td>
                <td style={{ textAlign: "center", whiteSpace: "normal" }}>
                  <button
                    className="success-action-button me-1"
                    id={`ViewToolTip-${suggestion._id || idx}`}
                    ref={(el) =>
                      (eyeButtonRefs.current[suggestion._id || idx] = el)
                    }
                  >
                    <i
                      className="fa fa-eye"
                      onClick={() => {
                        if (eyeButtonRefs.current[suggestion._id || idx]) {
                          eyeButtonRefs.current[suggestion._id || idx].blur();
                        }
                        setTooltipOpen((prev) => ({
                          ...prev,
                          [suggestion._id || idx]: false,
                        }));
                        handleView(suggestion);
                      }}
                      style={{ fontSize: "18px" }}
                    ></i>
                    <ToolTip
                      attrToolTip={{
                        placement: "top",
                        isOpen: tooltipOpen[suggestion._id || idx] || false,
                        target: `ViewToolTip-${suggestion._id || idx}`,
                        toggle: () => toggleTooltip(suggestion._id || idx),
                      }}
                    >
                      <strong>{t("View")}</strong>
                    </ToolTip>
                  </button>
                  {suggestion.investigation_suggestion_pdf && (
                    <button
                    onClick={() => handleViewPdf(suggestion)}
                      className="success-action-button me-1"                      
                      id={`PdfToolTip-${suggestion._id || idx}`}
                      ref={(el) =>
                        (pdfButtonRefs.current[suggestion._id || idx] = el)
                      }
                    >
                      <i
                        className="fa fa-file-pdf-o"
                        onClick={() => {
                          if (pdfButtonRefs.current[suggestion._id || idx]) {
                            pdfButtonRefs.current[suggestion._id || idx].blur();
                          }
                          setTooltipOpen((prev) => ({
                            ...prev,
                            [`pdf-${suggestion._id || idx}`]: false,
                          }));
                          
                        }}
                        style={{ fontSize: "18px" }}
                      ></i>
                      <ToolTip
                        attrToolTip={{
                          placement: "top",
                          isOpen:
                            tooltipOpen[`pdf-${suggestion._id || idx}`] ||
                            false,
                          target: `PdfToolTip-${suggestion._id || idx}`,
                          toggle: () =>
                            toggleTooltip(`pdf-${suggestion._id || idx}`),
                        }}
                      >
                        <strong>{t("View PDF")}</strong>
                      </ToolTip>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Model */}
      <Modal isOpen={modal} toggle={() => setModal(false)} size="lg">
        <div className="modal-header">
          <div className="d-flex justify-content-between w-100 align-items-center">
            <div className="d-flex justify-content-between w-100 align-items-end">
              <div className="d-flex flex-column">
                <h5 className="mb-1 modal-title">
                  Patient Name:&nbsp;
                  <strong>{selectedItem?.patient?.name || "N/A"}</strong>
                  {/* <strong>{selectedItem?.patient?.full_name || "N/A"}</strong> */}
                </h5>
                <small className="text-muted">
                  NAKBHO Health ID:&nbsp;
                  <strong>{selectedItem?.patient?.health_id?.match(/.{1,4}/g).join(" ") || "N/A"}</strong>
                </small>
                <small className="text-muted">
                  Gender:&nbsp;
                  <strong>
                    {selectedItem?.patient?.gender.charAt(0).toUpperCase() +
                      selectedItem?.patient?.gender.slice(1) || "N/A"}
                  </strong>
                </small>
              </div>
              <div>
                <small className="text-muted">
                  {formatDoctorDisplayDate(selectedItem?.createdAt, currentLab) || "-"}
                </small>
              </div>
            </div>

            <button
              type="button"
              className="btn-close"
              onClick={() => setModal(false)}
            ></button>
          </div>
        </div>
        <div className="modal-body">
          <div className="mb-3">
            <h6 className="mb-0">{t("Lab Test")}</h6>
          </div>

          {selectedItem?.investigation?.map((inv, invIndex) => (
            <div key={invIndex} className="mb-3">
              {inv.suggestion_reason && (
                <div className="mb-3">
                  <label className="form-label">
                    {t("Indication (Reason for test)")}
                  </label>
                  <div className="p-2 border rounded">
                    {inv.suggestion_reason}
                  </div>
                </div>
              )}

              {inv.suggestions?.map((suggestion, suggIndex) => (
                <div key={suggIndex} className="mb-3">
                  <div
                    className="p-2 mb-2 text-white rounded"
                    style={{ backgroundColor: "var(--theme-default)" }}
                  >
                    <strong>{suggestion.category_name}</strong>
                  </div>
                  <div className="flex-wrap gap-3 ps-3 d-flex">
                    {suggestion.examinations?.map((exam, examIndex) => (
                      <div
                        key={examIndex}
                        className="form-check d-flex align-items-center"
                        style={{ minWidth: "150px" }}
                      >
                        <input
                          className="form-check-input tw-w-5 tw-h-5"
                          type="checkbox"
                          checked={true}
                          id={`exam-${invIndex}-${suggIndex}-${examIndex}`}
                          style={{
                            backgroundColor: "var(--theme-default)",
                            borderColor: "var(--theme-default)",
                          }}
                        />
                        <label
                          className="mb-0 form-check-label ms-2"
                          htmlFor={`exam-${invIndex}-${suggIndex}-${examIndex}`}
                        >
                          {exam.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
        {showAddTest && (
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAddTest}
            >
              {t("Add Test")}
            </button>
          </div>
        )}
      </Modal>
    </>
  );
};

export default SuggestionTable;
